import React, { Fragment } from 'react'
import { Formik, FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { CircularProgress } from '@mui/material'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { CreateTeamRoleFormValues } from './CreateTeamRoleForm.interface'
import { showToast } from 'src/utils/toast'
import { CreateTeamRoleSchema } from './CreateTeamRoleForm.validator'
import { FormikPermissionsSelector } from './FormikPermissionsSelector'
import { ScrollToError } from 'src/components/shared/ScrollToError'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CreateTeamRole } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { getTeamPermissions } from 'src/selectors/team'
import { Button } from 'src/components/shared/Button'

const initialValues: CreateTeamRoleFormValues = {
  roleName: '',
  permissions: [],
}

interface Props {
  closeCreateTeamRoleModal(): void
  innerRef?: React.Ref<FormikProps<CreateTeamRoleFormValues>>
  isLoading: boolean
  refreshTeamRoles(): Promise<void>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const CreateTeamRoleForm = ({
  closeCreateTeamRoleModal,
  innerRef,
  isLoading,
  refreshTeamRoles,
  setIsLoading,
}: Props) => {
  const dispatch = useDispatch()

  const teamPermissions = useSelector(getTeamPermissions)

  const handleSubmit = async (values: CreateTeamRoleFormValues): Promise<void> => {
    if (!values.permissions.length) {
      showToast('Select at least one permission', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })

      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        CreateTeamRole.request({
          teamRoleName: values.roleName,
          permissions: values.permissions,
        }),
      )

      showToast(`New role “${values.roleName}” has been created`, {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })

      await refreshTeamRoles()

      closeCreateTeamRoleModal()
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      innerRef={innerRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={CreateTeamRoleSchema}
    >
      {(formikProps) => (
        <Fragment>
          <div css={tw`flex-1 overflow-y-auto px-4 pt-4`}>
            <div css={tw`grid grid-cols-1 gap-4 mb-4`}>
              <div>
                {isLoading || !teamPermissions ? (
                  <div css={tw`flex justify-center items-center h-full min-h-[300px]`}>
                    <CircularProgress size={48} />
                  </div>
                ) : (
                  <Fragment>
                    <ScrollToError />
                    <FormikTextField
                      name="roleName"
                      label="Role Name"
                      placeholder="Enter role name"
                      css={tw`w-full mb-4`}
                      inputProps={{
                        'data-testid': 'my-team-create-role-name-input',
                      }}
                    />
                    <FormikPermissionsSelector teamPermissions={teamPermissions} />
                  </Fragment>
                )}
              </div>
            </div>
          </div>
          <div css={tw`p-4 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}>
            <Button
              variant="contained"
              color="grey"
              size="small"
              onClick={closeCreateTeamRoleModal}
              loading={isLoading}
              className={tw`w-[99px]`}
              data-testid="my-team-create-role-form-close-btn"
            >
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => formikProps.submitForm()}
              loading={isLoading}
              className={tw`w-[150px]`}
              data-testid="my-team-create-role-form-submit-btn"
            >
              Create role
            </Button>
          </div>
        </Fragment>
      )}
    </Formik>
  )
}
